import { Link } from "react-router-dom";
import { ArrowUpRight, Activity, Compass, Sparkles } from "lucide-react";
import BodyCheckIn from "./BodyCheckIn";

const CARDS = [
  {
    id: "quiz",
    icon: Compass,
    eyebrow: "90-second quiz",
    title: "What's your element?",
    description:
      "Wood, Fire, Earth, Metal, or Water — find the element that's running your body right now, and the foods, points, and rituals that nourish it.",
    cta: "Take the quiz",
    to: "/quiz",
  },
  {
    id: "checkin",
    icon: Activity,
    eyebrow: "Daily 30-second tool",
    title: "How is your body today?",
    description:
      "A trauma-informed daily check-in. Six somatic states, a gentle next step for each, and a private history only you can see.",
    cta: "Check in",
    href: "#checkin",
  },
  {
    id: "reset",
    icon: Sparkles,
    eyebrow: "Free 7-day program",
    title: "The Nervous System Reset.",
    description:
      "One short ritual, every morning, for a week. Built from acupressure, breath, and the 5-element wisdom Joannie practices daily.",
    cta: "Start the 7-day reset",
    to: "/reset",
  },
];

export default function QuizAndResources() {
  return (
    <section
      id="resources"
      className="py-28 lg:py-36 border-y"
      style={{ background: "var(--sano-bg-3)", borderColor: "var(--sano-border)" }}
      data-testid="quiz-resources-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-8 mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">Quiz &amp; Resources</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Three small tools that meet you <em className="italic" style={{ color: "var(--sano-olive)" }}>between sessions</em>.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base leading-relaxed" style={{ color: "var(--sano-ink-2)" }}>
              Healing happens mostly outside the treatment room. These are the
              practices we hand you when you walk out the door — built so the
              work continues, quietly, every day.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-px" style={{ background: "var(--sano-border)" }}>
          {CARDS.map((c) => {
            const Icon = c.icon;
            const inner = (
              <>
                <Icon size={28} strokeWidth={1.25} style={{ color: "var(--sano-olive)" }} />
                <div className="eyebrow mt-6 mb-2">{c.eyebrow}</div>
                <h3 className="font-serif-display text-3xl leading-tight mb-4">{c.title}</h3>
                <p className="text-sm leading-relaxed mb-7 flex-1" style={{ color: "var(--sano-ink-2)" }}>
                  {c.description}
                </p>
                <span
                  className="inline-flex items-center gap-2 text-sm border-b border-current self-start pb-1 transition-all group-hover:gap-3"
                  style={{ color: "var(--sano-olive)" }}
                >
                  {c.cta} <ArrowUpRight size={14} strokeWidth={1.5} />
                </span>
              </>
            );
            const className =
              "p-10 lg:p-12 flex flex-col group transition-colors hover:bg-[#F0EBE1]";
            const style = { background: "var(--sano-bg)" };
            return c.to ? (
              <Link
                key={c.id}
                to={c.to}
                className={className}
                style={style}
                data-testid={`resource-card-${c.id}`}
              >
                {inner}
              </Link>
            ) : (
              <a
                key={c.id}
                href={c.href}
                className={className}
                style={style}
                data-testid={`resource-card-${c.id}`}
              >
                {inner}
              </a>
            );
          })}
        </div>
      </div>

      {/* Embedded body check-in below cards — same id #checkin keeps anchors working */}
      <div className="mt-20">
        <BodyCheckIn />
      </div>
    </section>
  );
}
